import React, { useEffect, useState, useRef } from 'react';

import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import { useSearchParams } from "react-router-dom";

import "../css/browse-table.css";
import SearchBox from "./search-box";
import ElementDialog from "./element-dialog";
import { apiUrl } from "../config/default";

import { tissueTranslate, backboneTranslate } from './dictionaries';

export default function BrowseTable() {

    let [tableData, setTableData] = useState<any>([]);
    let [searchParams, setSearchParams] = useSearchParams();
    const [filterValue, setFilterValue] = React.useState('');
    const [openExpDialog, setOpenExpDialog] = React.useState(false);
    const [expDialogData, setExpDialogData] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(false);
    const dt = useRef(null);
    const [tableFilters, setTableFilters] = useState<any>(null);

    const columns = [
        { field: 'organism', header: 'Organism' },
        { field: 'element_id', header: 'Element ID' },
        { field: 'description', header: 'Element Description' },
        { field: 'promoter', header: 'Promoter' },
        { field: 'transgenesis', header: 'Transgenesis' },
        { field: 'stage', header: 'Stage' },
        { field: 'curation_status', header: 'Expression' },
        { field: 'tissue_human_readable', header: 'Tissues' },
        { field: 'assembly', header: 'Genome Assembly' },
        { field: 'coord', header: 'Element Coordinates' }
    ];

    //first thing done: update page title
    useEffect(() => {
        document.title = 'Browse Experiments - Vista Enhancer Browser';
    }, []);

    useEffect(() => {
        if (!searchParams.get("filter") && !searchParams.get("advanced")) {
            loadData(null);
        }
    }, []);

    const loadData = (filterValue: any) => {
        setLoading(true);
        let url = apiUrl + "/catalogs/externalBrowse?" + filterValue;
        fetch(url)
            .then(res => res.json())
            .then(
                (result) => {
                    setLoading(false);
                    let processed_data = processDataFromApi(result.data);
                    setTableData(processed_data);
                    initFilters();
                },
                (error) => {
                    setLoading(false);
                }
            );
    };

    const processDataFromApi = (data: any[]) => {
        let processed_data = data;
        for (let i = 0; i < data.length; i += 1) {
            data[i]["element_id"] = data[i]["vista_id"]+"."+data[i]["allele_id"];
            data[i]["promoter"] = backboneTranslate(data[i]["backbone"], "promoter");
            data[i]["transgenesis"] = backboneTranslate(data[i]["backbone"], "transgenesisType");
            
            let tissues_hr = [];
            if(data[i]["tissue"] !== null) {
                let tissues = data[i]["tissue"].split(";");
                for (let j = 0; j < tissues.length; j += 1) {
                    tissues_hr.push(tissueTranslate(tissues[j], "label"))
                }
            }
            data[i]["tissue_human_readable"] = tissues_hr.join(", ");
        }
        return (processed_data);
    };

    const initFilters = () => {
        setTableFilters({
            'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
            'organism': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            'element_id': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'description': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            'promoter': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'transgenesis': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'stage': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'expression': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            'tissue_human_readable': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            'coord': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'coordinate_hg38': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'bracket_hg38': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'coordinate_mm10': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'bracket_mm10': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
        });
    };

    const elementCoordBodyTemplate = (rowData: any) => {
        if (!rowData.coord)
            return "Not Available";

        let url = "https://genome.ucsc.edu/cgi-bin/hgTracks?db=" + rowData.assembly + "&position=" + rowData.coord;
        return (
            <React.Fragment>
                <span><a href={url} target='_blank' rel='noreferrer'>{rowData.coord}</a></span>
            </React.Fragment>
        );

    };

    const humanCoordBodyTemplate = (rowData: any) => {
        if (!rowData.coordinate_hg38)
            return "Not Available";

        let url = "https://genome.ucsc.edu/cgi-bin/hgTracks?db=hg38&position=" + rowData.coordinate_hg38;
        return (
            <React.Fragment>
                <span><a href={url} target='_blank' rel='noreferrer'>{rowData.coordinate_hg38}</a></span>
            </React.Fragment>
        );

    };

    const mouseCoordBodyTemplate = (rowData: any) => {
        if (!rowData.coordinate_mm10)
            return "Not Available";

        let url = "https://genome.ucsc.edu/cgi-bin/hgTracks?db=mm10&position=" + rowData.coordinate_mm10;
        return (
            <React.Fragment>
                <span><a href={url} target='_blank' rel='noreferrer'>{rowData.coordinate_mm10}</a></span>
            </React.Fragment>
        );

    };

    const bracket_hg38BodyTemplate = (rowData: any) => {
        if (!rowData.bracket_hg38)
            return "Not Available";

        let genes = rowData.bracket_hg38.split("-");
        let api_search = "";
        if (genes.length > 1) {
            api_search = genes[0] + "[GN] OR " + genes[1] + "[GN]";
        }
        else {
            let genes_ed = genes[0].split(" ");
            api_search = genes_ed[0] + "[GN]";
        }
        let url = "https://www.ncbi.nlm.nih.gov/gene/?term=(" + api_search + ")+AND+9606%5BTaxonomy+ID%5D";
        return (
            <React.Fragment>
                <span><a href={url} target='_blank' rel='noreferrer'>{rowData.bracket_hg38}</a></span>
            </React.Fragment>
        );

    };

    const bracket_mm10BodyTemplate = (rowData: any) => {
        if (!rowData.bracket_mm10)
            return "Not Available";
        let genes = rowData.bracket_mm10.split("-");
        let api_search = "";
        if (genes.length > 1) {
            api_search = genes[0] + "[GN] OR " + genes[1] + "[GN]";
        }
        else {
            let genes_ed = genes[0].split(" ");
            api_search = genes_ed[0] + "[GN]";
        }
        let url = "https://www.ncbi.nlm.nih.gov/gene/?term=(" + api_search + ")+AND+10090%5BTaxonomy+ID%5D";
        return (
            <React.Fragment>
                <span><a href={url} target='_blank' rel='noreferrer'>{rowData.bracket_mm10}</a></span>
            </React.Fragment>
        );

    };

    const vista_idLinkTemplate = (rowData: any) => {
        if (!rowData || !rowData.vista_id)
            return null;
        let _key = Math.floor(Math.random() * 9999);
        return (<a key={_key}
            href={`/vista/element?vistaId=${rowData.vista_id}&alleleId=${rowData.allele_id}&backbone=${rowData.backbone}&stage=${rowData.stage}`}
            rel="noreferrer">
            {rowData.vista_id + "." + rowData.allele_id}
        </a>)
    }

    const stageTemplate = (rowData: any) => {
        if (!rowData || !rowData.stage)
            return null;
        let stage = rowData.stage.replaceAll(";", ", ");
        return (stage);
    }

    const organismTemplate = (rowData: any) => {
        if (!rowData || !rowData.organism)
            return null;
        return (<i>{rowData.organism}</i>);
    }

    const expressionsBodyTemplate = (rowData: any) => {
        if (!rowData || !rowData.curation_status)
            return null;

        //find duplicated stages and show the positive one if that exit
        let lookup: any = {};
        for (let expItem of rowData.curation_status) {
            if (expItem.stage in lookup &&
                lookup[expItem.stage].curation_status !== "positive") {
                lookup[expItem.stage].curation_status = expItem.curation_status;
                lookup[expItem.stage].data = expItem;
            }
            else if (!(expItem.stage in lookup)) {
                lookup[expItem.stage] = {
                    "curation_status": expItem.curation_status,
                    "data": expItem
                };
            }
        }

        //flatten filtered list
        let renderList = [];
        for (let key in lookup) {
            renderList.push(lookup[key].data);
        }

        return renderList.map((item: any) => {
            let circleColor = "orange";
            let textColor = "black";
            let text = "curated allele";
            if (rowData.curation_status && rowData.curation_status === "positive") {
                circleColor = "blue";
                textColor = "white";
                text = "positive";
            }
            else if (rowData.curation_status && rowData.curation_status === "negative") {
                circleColor = "grey";
                text = "negative";
            }
            let _key = Math.floor(Math.random() * 9999);
            return (<a key={_key}
                className="p-button p-button-rounded bt-expression-circle"
                style={{ "backgroundColor": circleColor, "color": textColor, textDecoration: "none" }}
                href={`/vista/element?vistaId=${rowData.vista_id}&alleleId=${rowData.allele_id}&backbone=${rowData.backbone}&stage=${rowData.stage}`}
                rel="noreferrer"
            >
                {text}
            </a>);

        });

    }

    const handleCloseExpDialog = (value: string) => {
        setOpenExpDialog(false);
    };

    const exportCSV = (selectionOnly: any) => {
        if (dt.current) {
            let dc: any = dt.current;
            dc.exportCSV(false);
        }
    }

    const dynamicColumns = columns.map((col, i) => {
        if (col.field === "coord") {
            return <Column key={col.field} field={col.field} header={col.header} body={elementCoordBodyTemplate} />;
        }
        if (col.field === "coordinate_hg38") {
            return <Column key={col.field} field={col.field} header={col.header} body={humanCoordBodyTemplate} />;
        }
        else if (col.field === "coordinate_mm10") {
            return <Column key={col.field} field={col.field} header={col.header} body={mouseCoordBodyTemplate} />;
        }
        else if (col.field === "bracket_hg38") {
            return <Column key={col.field} field={col.field} header={col.header} body={bracket_hg38BodyTemplate} />;
        }
        else if (col.field === "bracket_mm10") {
            return <Column key={col.field} field={col.field} header={col.header} body={bracket_mm10BodyTemplate} />;
        }
        else if (col.field === "curation_status") {
            return <Column key={col.field} field={col.field} header={col.header} body={expressionsBodyTemplate} />;
        }
        else if (col.field === "element_id") {
            return <Column key={col.field} field={col.field} header={col.header} body={vista_idLinkTemplate} />;
        }
        else if (col.field === "stage") {
            return <Column key={col.field} field={col.field} header={col.header} body={stageTemplate} />;
        }
        else if (col.field === "organism") {
            return <Column key={col.field} field={col.field} header={col.header} body={organismTemplate} />;
        }
        else {
            return <Column key={col.field} field={col.field} header={col.header} />;
        }
    });
    const headerGroup = <ColumnGroup>
        <Row>
            <Column header=" " headerStyle={{ width: '3rem' }} />
            <Column header="Organism" field='organism' sortable filter={true} filterField="organism" filterPlaceholder="Filter by Organism..." />
            <Column header="ID" field='element_id' sortable filter={true} filterField="element_id" filterPlaceholder="Filter by Element ID..." />
            <Column header="Element Description" field='description' sortable filter={true} filterField="description" filterPlaceholder="Filter by description..." />
            <Column header="Promoter" field='promoter' sortable filter={true} filterField="promoter" filterPlaceholder="Filter by promoter..." />
            <Column header="Transgenesis" field='transgenesis' sortable={false} filter={true} filterField="transgenesis" filterPlaceholder="Filter by transgenesis..." />
            <Column header="Stage" field='stage' sortable filter={true} filterField="stage" filterPlaceholder="Filter by stages..." />
            <Column header="Expression" field='expression' sortable={false} filter={true} filterField="expression" filterPlaceholder="Search by expression..." />
            <Column header="Tissues" field='tissue_human_readable' sortable filter={true} filterField="tissue_human_readable" filterPlaceholder="Filter by tissue..." />
            <Column header="Genome Assembly" field='assembly' sortable filter={true} filterField="assembly" filterPlaceholder="Filter by genome assembly..." />
            <Column header="Element Coordinates" field='coord' sortable filter={true} filterField="coord" filterPlaceholder="Filter by coordinates..." />
        </Row>

    </ColumnGroup>;

    const affectHeightStyle = () => {
        var style = {}
        if (tableData.length < 10) {
            style = { "minHeight": "85vh" }
        }
        return (style)
    }

    return (
        <main className='px-5' style={affectHeightStyle()}>
            <br />
            <Container style={{ display: '' }} maxWidth='lg'>
                <SearchBox loadData={loadData}></SearchBox>
            </Container>

            <br />
            <br />

            {loading && (
                <div style={{ background: "white", opacity: "0.8", zIndex: 100, position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}>
                    <CircularProgress
                        variant="indeterminate"
                        disableShrink
                        size={100}
                        sx={{
                            position: 'absolute',
                            marginLeft: "auto",
                            marginRight: "auto",
                            left: 0,
                            right: 0,
                            top: "50%",
                            textAlign: "center",
                            zIndex: 111
                        }}
                    />
                </div>
            )}

            <ElementDialog open={openExpDialog}
                onClose={handleCloseExpDialog}
                data={expDialogData}></ElementDialog>
            <div>
                <div className=''>
                    <Button type="button" onClick={() => exportCSV(true)} className="btn btn-success float-end" data-pr-tooltip="Selection Only"><i className='pi pi-download'> </i> &nbsp; Download Data</Button><br></br>
                </div>
                <br />
                <div className='row'>
                    <DataTable ref={dt}
                        value={tableData}
                        rowGroupMode="rowspan"
                        headerColumnGroup={headerGroup}
                        sortField="element_id"
                        sortOrder={1}

                        responsiveLayout="scroll"
                        stripedRows={true}
                        filters={tableFilters}
                        filterDisplay="menu"

                        paginator
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50, 100, 500, 1000]}
                        paginatorPosition='bottom'
                    >
                        <Column header=" " headerStyle={{ width: '3rem' }} body={(data, options) => options.rowIndex + 1}></Column>
                        {dynamicColumns}

                    </DataTable>
                </div>
            </div>

        </main>
    );
}